import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { inr, fmtDate } from "../lib/format";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

const TXN_TYPES = ["Investment", "Additional Investment", "Withdrawal", "Redemption",
  "Dividend", "Interest", "Fee", "Other"];
const FEE_TYPES = ["Onboarding Fee", "Exit Fee", "Profit Sharing", "Purchase Charge", "Other Fee"];

const In = ({ label, value, onChange, type = "text", testId }) => (
  <div className="space-y-1.5">
    <label className="label-caps">{label}</label>
    <input type={type} value={value ?? ""} data-testid={testId}
      onChange={(e) => onChange(e.target.value)} className="glass-input" />
  </div>
);

const Sel = ({ label, value, onChange, options, testId }) => (
  <div className="space-y-1.5">
    <label className="label-caps">{label}</label>
    <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testId}
      className="glass-input appearance-none cursor-pointer [&>option]:bg-[#0A110D]">
      {options.map((o) => <option key={o} value={o}>{o || "—"}</option>)}
    </select>
  </div>
);

export default function TxnEditDialog({ txn, onClose, onSaved }) {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (txn) {
      setF({
        date: (txn.date || "").slice(0, 10), type: txn.type, description: txn.description || "",
        amount: txn.amount, reference_number: txn.reference_number || "", status: txn.status || "Completed",
        remarks: txn.remarks || "", withdrawal_type: txn.withdrawal_type || "", fee_type: txn.fee_type || "",
        profit_sharing_pct: txn.profit_sharing_pct ?? "", exit_fee_pct: txn.exit_fee_pct ?? "", reason: "",
      });
    }
  }, [txn]);

  if (!txn || !f) return null;
  const s = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!f.reason.trim()) { toast.error("A reason for correction is required (audit trail)."); return; }
    if (!f.date) { toast.error("Date is required."); return; }
    setBusy(true);
    try {
      const payload = { date: f.date, type: f.type, description: f.description, amount: Number(f.amount),
        reference_number: f.reference_number || null, status: f.status, remarks: f.remarks || null,
        reason: f.reason.trim() };
      if (f.type === "Withdrawal") {
        payload.withdrawal_type = f.withdrawal_type || null;
        if (f.withdrawal_type === "Return" && f.profit_sharing_pct !== "") payload.profit_sharing_pct = Number(f.profit_sharing_pct);
        if (f.withdrawal_type === "Capital" && f.exit_fee_pct !== "") payload.exit_fee_pct = Number(f.exit_fee_pct);
      }
      if (f.type === "Fee") payload.fee_type = f.fee_type || null;
      await api.patch(`/admin/transactions/${txn.id}`, payload);
      toast.success("Transaction updated — all dependent values recalculated.");
      onSaved();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const history = txn.edit_history || [];

  return (
    <Dialog open={!!txn} onOpenChange={onClose}>
      <DialogContent className="border-white/10 bg-[#0A110D]/95 backdrop-blur-2xl max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="txn-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-white">Edit Transaction</DialogTitle>
          <DialogDescription className="text-zinc-500">
            {txn.investor_name} · {fmtDate(txn.date)} · {inr(txn.amount)}. Corrections recalculate invested value, AUM, withdrawals and fees everywhere. The original values are preserved in the audit trail.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <In label="Date" type="date" value={f.date} onChange={s("date")} testId="te-date" />
          <Sel label="Transaction Type" value={f.type} onChange={(v) => setF((p) => ({ ...p, type: v, withdrawal_type: "", fee_type: "" }))} options={TXN_TYPES} testId="te-type" />
          <div className="sm:col-span-2"><In label="Description" value={f.description} onChange={s("description")} testId="te-description" /></div>
          <In label="Amount (₹)" type="number" value={f.amount} onChange={s("amount")} testId="te-amount" />
          <In label="Reference Number" value={f.reference_number} onChange={s("reference_number")} testId="te-reference" />
          {f.type === "Withdrawal" && (
            <Sel label="Withdrawal Type" value={f.withdrawal_type} onChange={s("withdrawal_type")}
              options={["", "Capital", "Return"]} testId="te-withdrawal-type" />
          )}
          {f.type === "Withdrawal" && f.withdrawal_type === "Return" && (
            <In label="Profit Sharing %" type="number" value={f.profit_sharing_pct} onChange={s("profit_sharing_pct")} testId="te-profit-sharing" />
          )}
          {f.type === "Withdrawal" && f.withdrawal_type === "Capital" && (
            <In label="Exit Load %" type="number" value={f.exit_fee_pct} onChange={s("exit_fee_pct")} testId="te-exit-fee" />
          )}
          {f.type === "Fee" && (
            <Sel label="Fee Type" value={f.fee_type} onChange={s("fee_type")} options={["", ...FEE_TYPES]} testId="te-fee-type" />
          )}
          <Sel label="Status" value={f.status} onChange={s("status")} options={["Completed", "Pending", "Failed"]} testId="te-status" />
          <In label="Remarks" value={f.remarks} onChange={s("remarks")} testId="te-remarks" />
          <div className="sm:col-span-2 space-y-1.5">
            <label className="label-caps">Reason for Correction <span className="text-red-400">*</span></label>
            <textarea rows={2} value={f.reason} onChange={(e) => s("reason")(e.target.value)}
              data-testid="te-reason" className="glass-input h-auto py-2.5 resize-none"
              placeholder="e.g. Amount entered incorrectly at data entry" />
          </div>
        </div>
        {history.length > 0 && (
          <div className="mt-4 border-t border-white/[0.06] pt-4" data-testid="te-history">
            <p className="label-caps mb-2">Edit History</p>
            <div className="space-y-2">
              {history.map((h, i) => (
                <div key={i} className="text-[11px] text-zinc-500 border border-white/[0.06] bg-white/[0.02] rounded-lg px-3 py-2">
                  <span className="text-zinc-300">{h.by}</span> · {fmtDate(h.at)} — {h.reason}
                  <div className="mt-1">
                    {(h.changes || []).map((c, j) => (
                      <span key={j} className="tnum mr-3">{c.field}: {String(c.old ?? "—")} → {String(c.new ?? "—")}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        <Button onClick={submit} disabled={busy} data-testid="te-save"
          className="w-full bg-emerald-500 hover:bg-emerald-400 text-[#030706] font-bold mt-4">
          {busy ? "Saving..." : "Save Correction"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
